/**
 * Sync tasks serveur ↔ local — Phase 4, incrément 4.7.
 *
 * Symétrique d'`events-sync.ts` (4.6). Étend la stratégie
 * server-wins (ADR 0009) aux tasks nested dans
 * `home.data.tasks`. Déclenché par `homes-sync.ts` après que
 * les homes ont leurs UUIDs serveur.
 *
 * Mode local-only préservé : si l'API échoue, le state local
 * reste utilisable.
 */
import * as tasksApi from '../api/tasks.js';
import { ApiError } from '../api/client.js';
import {
  applyTasksSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerTaskPayload,
} from './homes.js';
import type { ClassicHomeData, FamilyTask } from '@nestor/shared-types';

export interface TasksSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: TasksSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getTasksSyncStatus(): TasksSyncStatus {
  return status;
}

/**
 * Synchronise les tasks pour tous les foyers UUID classiques.
 * Idempotent (dédup via `inFlight`).
 *
 * Pour chaque home :
 *   1. POST les tasks locales pré-auth (id non-UUID) → idMap.
 *   2. GET la liste serveur.
 *   3. `applyTasksSyncResult(homeId, …)` atomique.
 *
 * Erreurs ponctuelles : warn + continue avec les autres homes.
 */
export async function syncTasksForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'classic') continue;

    const data = home.data as ClassicHomeData;

    // 1. Migration POST des tasks locales pré-auth.
    //
    // Phase 3.3.3 : offline-first via SendResult. `kind: 'sent'` =
    // remap idMap ; `kind: 'queued'` = on garde le local id, le
    // replay au prochain online event re-tentera.
    const idMap = new Map<string, string>();
    const localOnly = data.tasks.filter((t) => !isServerHomeId(t.id));
    for (const task of localOnly) {
      try {
        const result = await tasksApi.createTask(home.id, buildCreateInput(task), task.id);
        if (result.kind === 'sent') {
          idMap.set(task.id, result.data.task.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[tasks-sync] migration POST échouée', {
          homeId: home.id,
          taskId: task.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverTasks = await tasksApi.listTasks(home.id);
      const payload: ServerTaskPayload[] = serverTasks.map((t) => ({
        id: t.id,
        text: t.text,
        due: t.due,
        done: t.done,
      }));
      applyTasksSyncResult(home.id, { idMap, serverTasks: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyTasksSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[tasks-sync] fetch /tasks échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(task: FamilyTask): tasksApi.CreateTaskInput {
  return {
    text: task.text,
    ...(task.due ? { due: task.due } : {}),
    // `done` envoyé seulement s'il est `true` — sinon le default
    // serveur (false) joue.
    ...(task.done ? { done: true } : {}),
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetTasksSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
